import { dashboardApi } from '@/store/api/base/api.base';
import { walkPages } from '@/store/api/base/api.walk';
import { dropRecurring, writeRecurring } from '@/store/api/recurring/recurring.cache';
import type { TypeBulkSkip } from '@/types/list/types.list.bulk';
import type { TypeRecurring, TypeRecurringInput, TypeRecurringStatus } from '@/types/recurring/types.recurring';

type TypeRecurringRunReport = { created: string[]; skipped: string[]; plan: TypeRecurring | null };

type TypeRecurringBulkStatusResult = {
    updated: TypeRecurring[];
    unchanged: string[];
    skipped: TypeBulkSkip[];
};

type TypeRecurringBulkRemoveResult = {
    removed: string[];
    skipped: TypeBulkSkip[];
};

const PAGE_SIZE = 200;

const MAX_PAGES = 10;

export const byNext = (a: TypeRecurring, b: TypeRecurring) => (a.nextDate || '9999').localeCompare(b.nextDate || '9999') || a.name.localeCompare(b.name, 'it');

export const recurringApi = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        listRecurrings: build.query<TypeRecurring[], void>({
            queryFn: async () => {
                const walked = await walkPages<TypeRecurring>({
                    url: '/recurrings',
                    fallback: 'Non è stato possibile caricare i canoni',
                    pageSize: PAGE_SIZE,
                    maxPages: MAX_PAGES,
                });
                if ('error' in walked) return walked;
                const items = walked.data;
                return { data: items.sort(byNext) };
            },
            providesTags: ['Recurring'],
        }),

        createRecurring: build.mutation<TypeRecurring, { recurring: TypeRecurringInput; clientRef: string }>({
            query: ({ recurring, clientRef }) => ({ url: '/recurrings', method: 'post', data: { recurring, clientRef } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try { writeRecurring(dispatch, (await queryFulfilled).data); } catch { return; }
            },
        }),

        updateRecurring: build.mutation<TypeRecurring, { id: string; recurring: TypeRecurringInput; revision: number }>({
            query: ({ id, recurring, revision }) => ({ url: `/recurrings/${id}`, method: 'patch', data: { recurring, revision } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try { writeRecurring(dispatch, (await queryFulfilled).data); } catch { return; }
            },
        }),

        setRecurringStatus: build.mutation<TypeRecurring, { id: string; status: TypeRecurringStatus }>({
            query: ({ id, status }) => ({ url: `/recurrings/${id}/status`, method: 'patch', data: { status } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try { writeRecurring(dispatch, (await queryFulfilled).data); } catch { return; }
            },
        }),

        runRecurring: build.mutation<TypeRecurringRunReport, string>({
            query: (id) => ({ url: `/recurrings/${id}/run`, method: 'post' }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    if (data.plan) writeRecurring(dispatch, data.plan);
                } catch { return; }
            },
        }),

        setRecurringStatusMany: build.mutation<TypeRecurringBulkStatusResult, { ids: string[]; status: TypeRecurringStatus }>({
            query: ({ ids, status }) => ({ url: '/recurrings/status', method: 'patch', data: { ids, status } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    for (const recurring of data.updated) writeRecurring(dispatch, recurring);
                } catch { return; }
            },
        }),

        removeRecurringMany: build.mutation<TypeRecurringBulkRemoveResult, string[]>({
            query: (ids) => ({ url: '/recurrings', method: 'delete', data: { ids } }),
            onQueryStarted: async (_ids, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    if (!data.removed.length) return;
                    const gone = new Set(data.removed);
                    dispatch(recurringApi.util.updateQueryData('listRecurrings', undefined, (draft) => draft.filter((entry) => !gone.has(entry.id))));
                } catch { return; }
            },
        }),

        removeRecurring: build.mutation<{ id: string }, string>({
            query: (id) => ({ url: `/recurrings/${id}`, method: 'delete' }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
                try { await queryFulfilled; dropRecurring(dispatch, id); } catch { return; }
            },
        }),
    }),
});

export const {
    useListRecurringsQuery, useCreateRecurringMutation, useUpdateRecurringMutation, useSetRecurringStatusMutation, useRunRecurringMutation, useRemoveRecurringMutation,
    useSetRecurringStatusManyMutation, useRemoveRecurringManyMutation,
} = recurringApi;

export default recurringApi;
